import * as React from "react";

import {
    DataTable,
    ColumnBuilder,
    SelectionToolSpec,
    buildToolbarWithViewAdjuster,
    DataTableMode,
} from "@impero-com/common/components/dataTable";
import { TableFilter } from "@impero-com/common/components/filter";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import * as DV from "@impero-com/common/utils/dataView";
import { t } from "@impero-com/common/utils/i18n";
import {
    DataViewIndex,
    QueryResult,
    SimpleAndFilter,
    SimpleFieldFilter,
    addFieldFilter,
    removeFieldFilter,
} from "@impero-com/common/utils/indexing";
import { assertNever } from "@impero-com/common/utils/obj";
import { OrgCookie } from "@impero-com/common/utils/orgCookie";
import { ContentWithoutBorder } from "@impero-com/ui";
import memoizeOne from "memoize-one";

import type { UserProfile } from "api/generated/model";
import { AvailableActions } from "api/generated/model";
import * as theme from "org/user/list/theme.css";
import { WrappedFormUtils } from "utils/form";

import { ExportFormContext } from "./controls/export";
import { ExportPopup } from "./exportPopup";
import { ResourcesDrawer } from "./resourcesDrawer";
import { ExportFormSchema } from "./schema";

interface ControlResourcesProps<
    ITEM,
    E,
    ChangeDetailsFormValues,
    ManageAccessRightsFormValues,
    ModifySchedulingFormValues,
> {
    ajaxManager: AjaxManager;
    availableActions: AvailableActions;
    orgCookie: OrgCookie;
    orgProfileCookie: UserProfile | null;
    dataView: DV.DataView<ITEM>;
    dataViewIndex: DataViewIndex<ITEM>;
    columnBuilders?: { [extractorId: string]: ColumnBuilder<ITEM, any> };
    onExpand?: (item: ITEM, index: number) => ExpandedRow<E>;
    renderExpandedRow?: (
        item: ITEM,
        index: number
    ) => React.ReactNode | undefined;
    getRowClassName?: (item: ITEM, isActiveRow: boolean) => string;
    tableMode?: DataTableMode;
    adjustable?: boolean;
    filter?: SimpleAndFilter | null;
    onFilterChanged?: (filter: SimpleAndFilter | null) => void;
    visibleColumnIds?: Set<string> | null;
    onVisibleColumnsChanged?: (visibleColumnIds: Set<string>) => void;
    exportAction?: ExportAction<ITEM>;
    changeDetails?: {
        canEditItem: (item: ITEM) => boolean;
        readOnlyInfo: string;
        renderDrawerBody: (
            selectedItems: Array<ITEM>,
            readOnly: boolean,
            form: WrappedFormUtils<
                ChangeDetailsFormValues,
                ChangeDetailsFormValues
            >
        ) => React.ReactNode;
        confirmationTitle: string;
        confirmationText: (selectedIds: Array<string>) => string;
        onSave: (
            selectedIds: Array<string>,
            form: WrappedFormUtils<
                ChangeDetailsFormValues,
                ChangeDetailsFormValues
            >,
            done: (closeDrawer: boolean) => void
        ) => void;
    };
    manageAccessRights?: {
        canAdminItem: (item: ITEM) => boolean;
        readOnlyInfo: string;
        renderDrawerBody: (
            selectedItems: Array<ITEM>,
            readOnly: boolean,
            form: WrappedFormUtils<
                ManageAccessRightsFormValues,
                ManageAccessRightsFormValues
            >
        ) => React.ReactNode;
        confirmationTitle: string;
        confirmationText: (selectedIds: Array<string>) => string;
        onSave: (
            selectedIds: Array<string>,
            form: WrappedFormUtils<
                ManageAccessRightsFormValues,
                ManageAccessRightsFormValues
            >,
            done: (closeDrawer: boolean) => void
        ) => void;
    };
    modifySchedule?: {
        canEditItem: (item: ITEM) => boolean;
        readOnlyInfo: string;
        renderDrawerBody: (
            selectedItems: Array<ITEM>,
            readOnly: boolean,
            form: WrappedFormUtils<
                ModifySchedulingFormValues,
                ModifySchedulingFormValues
            >
        ) => React.ReactNode;
        confirmationTitle: string;
        confirmationText: (selectedIds: Array<string>) => string;
        onSave: (
            selectedIds: Array<string>,
            form: WrappedFormUtils<
                ModifySchedulingFormValues,
                ModifySchedulingFormValues
            >,
            done: (closeDrawer: boolean) => void
        ) => void;
    };
}

interface ExportAction<ITEM> {
    title: string;
    onSave: (
        selectedItems: Array<string>,
        form: ExportFormSchema,
        ctx: ExportFormContext<ITEM>,
        done: () => void
    ) => void;
}

interface ExpandedRow<E> {
    dataView: DV.DataView<E>;
    columnBuilders: { [extractorId: string]: ColumnBuilder<E, any> };
}

interface ControlResourcesState<
    ChangeDetailsFormValues,
    ManageAccessRightsFormValues,
    ModifySchedulingFormValues,
> {
    filter: SimpleAndFilter | null;
    popup: ExportPopup | undefined;
    drawer:
        | ChangeDetailsDrawer<ChangeDetailsFormValues>
        | ManageAccessRightsDrawer<ManageAccessRightsFormValues>
        | ModifyScheduleDrawer<ModifySchedulingFormValues>
        | undefined;
}

interface ExportPopup {
    type: "EXPORT";
    selectedIds: Array<string>;
    title: string;
    onConfirm: (
        selectedIds: string[],
        form: ExportFormSchema,
        done: () => void
    ) => void;
}

interface ChangeDetailsDrawer<ChangeDetailsFormValues> {
    type: "DETAILS";
    readOnly: boolean;
    selectedIds: Array<string>;
    readOnlyInfo: string;
    renderBody: (
        form: WrappedFormUtils<ChangeDetailsFormValues, ChangeDetailsFormValues>
    ) => React.ReactNode;
    confirmationTitle: string;
    confirmationText: string;
    onSave: (
        selectedIds: Array<string>,
        form: WrappedFormUtils<
            ChangeDetailsFormValues,
            ChangeDetailsFormValues
        >,
        done: (closeDrawer: boolean) => void
    ) => void;
}

interface ManageAccessRightsDrawer<ManageAccessRightsFormValues> {
    type: "ACCESS_RIGHTS";
    readOnly: boolean;
    selectedIds: Array<string>;
    readOnlyInfo: string;
    renderBody: (
        form: WrappedFormUtils<
            ManageAccessRightsFormValues,
            ManageAccessRightsFormValues
        >
    ) => React.ReactNode;
    confirmationTitle: string;
    confirmationText: string;
    onSave: (
        selectedIds: Array<string>,
        form: WrappedFormUtils<
            ManageAccessRightsFormValues,
            ManageAccessRightsFormValues
        >,
        done: (closeDrawer: boolean) => void
    ) => void;
}

interface ModifyScheduleDrawer<ModifySchedulingFormValues> {
    type: "MODIFY_SCHEDULE";
    readOnly: boolean;
    selectedIds: Array<string>;
    readOnlyInfo: string;
    renderBody: (
        form: WrappedFormUtils<
            ModifySchedulingFormValues,
            ModifySchedulingFormValues
        >
    ) => React.ReactNode;
    confirmationTitle: string;
    confirmationText: string;
    onSave: (
        selectedIds: Array<string>,
        form: WrappedFormUtils<
            ModifySchedulingFormValues,
            ModifySchedulingFormValues
        >,
        done: (closeDrawer: boolean) => void
    ) => void;
}

class ControlResources<
    ITEM,
    E,
    ChangeDetailsFormValues,
    ManageAccessRightsFormValues,
    ModifySchedulingFormValues,
> extends React.PureComponent<
    ControlResourcesProps<
        ITEM,
        E,
        ChangeDetailsFormValues,
        ManageAccessRightsFormValues,
        ModifySchedulingFormValues
    >,
    ControlResourcesState<
        ChangeDetailsFormValues,
        ManageAccessRightsFormValues,
        ModifySchedulingFormValues
    >
> {
    constructor(
        props: ControlResourcesProps<
            ITEM,
            E,
            ChangeDetailsFormValues,
            ManageAccessRightsFormValues,
            ModifySchedulingFormValues
        >
    ) {
        super(props);
        this.state = {
            filter: props.filter ? props.filter : null,
            drawer: undefined,
            popup: undefined,
        };
    }

    private buildQueryResult = memoizeOne(
        (
            dataView: DV.DataView<ITEM>,
            dataViewIndex: DataViewIndex<ITEM>,
            filter: SimpleAndFilter | null
        ): QueryResult<ITEM> => {
            if (filter) {
                return dataViewIndex.find(filter);
            } else {
                return {
                    dataView,
                    orResults: [],
                    excludedItemIds: null,
                };
            }
        }
    );

    private detailsDrawerData = memoizeOne(
        (
            dataSource: DV.DataSource<ITEM>,
            selectedIds: Array<string>,
            canEditItem: (item: ITEM) => boolean
        ): {
            selectedIds: Array<string>;
            selectedItems: Array<ITEM>;
            readOnly: boolean;
        } => {
            let count = 0;
            let readOnly = false;
            const selectedIdSet = new Set(selectedIds);
            const selectedItems: Array<ITEM> = [];
            DV.forEach(dataSource.iter(), (item) => {
                const itemId = dataSource.getId(item);
                if (selectedIdSet.has(itemId)) {
                    count += 1;
                    if (!canEditItem(item)) {
                        readOnly = true;
                    }
                    selectedItems.push(item);
                }
            });
            if (count !== selectedIds.length) {
                throw new Error(
                    `Expected ${selectedIds.length} editable items, got ${count}`
                );
            }
            return {
                selectedIds,
                selectedItems,
                readOnly,
            };
        }
    );

    private detailsPopupData = memoizeOne(
        (
            dataSource: DV.DataSource<ITEM>,
            selectedIds: Array<string>
        ): { selectedIds: Array<string>; selectedItems: Array<ITEM> } => {
            const selectedIdSet = new Set(selectedIds);
            const selectedItems: Array<ITEM> = [];
            DV.forEach(dataSource.iter(), (item) => {
                const itemId = dataSource.getId(item);
                if (selectedIdSet.has(itemId)) {
                    selectedItems.push(item);
                }
            });
            if (selectedIdSet.size !== selectedIds.length) {
                throw new Error(
                    `Expected ${selectedIds.length} editable items, got ${selectedIdSet.size}`
                );
            }
            return {
                selectedIds,
                selectedItems,
            };
        }
    );

    render = () => {
        const {
            adjustable,
            dataView,
            dataViewIndex,
            columnBuilders,
            onExpand,
            tableMode,
            exportAction,
            changeDetails,
            manageAccessRights,
            modifySchedule,
        } = this.props;
        const { filter } = this.state;
        const queryResult = this.buildQueryResult(
            dataView,
            dataViewIndex,
            filter
        );
        const tools: Array<SelectionToolSpec<ITEM>> = [];

        const renderExpandedRow =
            this.props.renderExpandedRow || this.renderExpandedRow;

        if (changeDetails) {
            const { canEditItem } = changeDetails;
            tools.push({
                isEnabled: (_formattedView, selectedIds, _visibleColumns) => {
                    return selectedIds.length > 0;
                },
                type: "Text",
                label: t("ChangeDetails"),
                onCommand: (
                    _onSelectionActionDone,
                    _formattedView,
                    selectedIds,
                    _visibleColumnIds
                ) => {
                    const drawerData = this.detailsDrawerData(
                        queryResult.dataView.dataSource,
                        selectedIds,
                        canEditItem
                    );
                    this.setState({
                        drawer: {
                            type: "DETAILS",
                            readOnly: drawerData.readOnly,
                            selectedIds: drawerData.selectedIds,
                            readOnlyInfo: changeDetails.readOnlyInfo,
                            renderBody: (form) => {
                                return changeDetails.renderDrawerBody(
                                    drawerData.selectedItems,
                                    drawerData.readOnly,
                                    form
                                );
                            },
                            confirmationTitle: changeDetails.confirmationTitle,
                            confirmationText:
                                changeDetails.confirmationText(selectedIds),
                            onSave: changeDetails.onSave,
                        },
                    });
                },
            });
        }
        if (manageAccessRights) {
            const { canAdminItem } = manageAccessRights;
            tools.push({
                isEnabled: (_formattedView, selectedIds, _visibleColumns) => {
                    return selectedIds.length > 0;
                },
                type: "Text",
                label: t("ManageAccessRights"),
                onCommand: (
                    _onSelectionActionDone,
                    _formattedView,
                    selectedIds,
                    _visibleColumnIds
                ) => {
                    const drawerData = this.detailsDrawerData(
                        queryResult.dataView.dataSource,
                        selectedIds,
                        canAdminItem
                    );
                    this.setState({
                        drawer: {
                            type: "ACCESS_RIGHTS",
                            readOnly: drawerData.readOnly,
                            selectedIds: drawerData.selectedIds,
                            readOnlyInfo: manageAccessRights.readOnlyInfo,
                            renderBody: (form) => {
                                return manageAccessRights.renderDrawerBody(
                                    drawerData.selectedItems,
                                    drawerData.readOnly,
                                    form
                                );
                            },
                            confirmationTitle:
                                manageAccessRights.confirmationTitle,
                            confirmationText:
                                manageAccessRights.confirmationText(
                                    selectedIds
                                ),
                            onSave: manageAccessRights.onSave,
                        },
                    });
                },
            });
        }
        if (modifySchedule) {
            const { canEditItem } = modifySchedule;
            tools.push({
                isEnabled: (_formattedView, selectedIds, _visibleColumns) => {
                    return selectedIds.length > 0;
                },
                type: "Text",
                label: t("ModifySchedule"),
                onCommand: (
                    _onSelectionActionDone,
                    _formattedView,
                    selectedIds,
                    _visibleColumnIds
                ) => {
                    const drawerData = this.detailsDrawerData(
                        queryResult.dataView.dataSource,
                        selectedIds,
                        canEditItem
                    );
                    this.setState({
                        drawer: {
                            type: "MODIFY_SCHEDULE",
                            readOnly: drawerData.readOnly,
                            selectedIds: drawerData.selectedIds,
                            readOnlyInfo: modifySchedule.readOnlyInfo,
                            renderBody: (form) => {
                                return modifySchedule.renderDrawerBody(
                                    drawerData.selectedItems,
                                    drawerData.readOnly,
                                    form
                                );
                            },
                            confirmationTitle: modifySchedule.confirmationTitle,
                            confirmationText:
                                modifySchedule.confirmationText(selectedIds),
                            onSave: modifySchedule.onSave,
                        },
                    });
                },
            });
        }
        if (exportAction) {
            tools.push({
                isEnabled: (_formattedView, selectedIds, _visibleColumns) => {
                    return selectedIds.length > 0;
                },
                type: "Text",
                label: t("Controls_Export"),
                onCommand: (
                    _onSelectionActionDone,
                    formattedView,
                    selectedIds,
                    visibleColumnIds
                ) => {
                    const popupData = this.detailsPopupData(
                        queryResult.dataView.dataSource,
                        selectedIds
                    );
                    const ctx = {
                        formattedView,
                        visibleColumnIds,
                    };
                    this.setState({
                        popup: {
                            type: "EXPORT",
                            selectedIds: popupData.selectedIds,
                            title: exportAction.title,
                            onConfirm: (selected, form, done) =>
                                exportAction.onSave(selected, form, ctx, done),
                        },
                    });
                },
            });
        }

        return (
            <ContentWithoutBorder className={theme.whiteBg}>
                <TableFilter<ITEM>
                    id="filter"
                    queryResult={queryResult}
                    dataViewIndex={dataViewIndex}
                    filter={filter}
                    extractorRefs={DV.getAllExtractors(this.props.dataView).map(
                        (ext) => ({ extractorId: ext.id })
                    )}
                    onAddFilter={this.addFilter}
                    onRemoveFilter={this.removeFilter}
                    onResetFilter={this.resetFilter}
                />
                <DataTable<ITEM>
                    id="control-resources"
                    mode={tableMode}
                    selectionHandler={
                        tools.length > 0
                            ? {
                                  tools,
                              }
                            : undefined
                    }
                    tools={
                        adjustable ? buildToolbarWithViewAdjuster() : undefined
                    }
                    expandedRowRender={
                        onExpand || this.props.renderExpandedRow
                            ? renderExpandedRow
                            : undefined
                    }
                    expandedRowClassName={this.getExpandedRowClassName}
                    dataView={queryResult.dataView}
                    columnBuilders={columnBuilders}
                    visibleColumnIds={this.props.visibleColumnIds}
                    onVisibleColumnsChanged={this.props.onVisibleColumnsChanged}
                    getRowClassName={this.props.getRowClassName}
                />
                {this.buildDrawer()}
                {this.buildPopup()}
            </ContentWithoutBorder>
        );
    };

    private addFilter = (
        extractorId: string,
        filter: SimpleFieldFilter,
        excluded: boolean
    ) => {
        const newFilter = addFieldFilter(
            extractorId,
            filter,
            excluded,
            this.state.filter
        );
        this.setState({ filter: newFilter });
        if (this.props.onFilterChanged) {
            this.props.onFilterChanged(newFilter);
        }
    };

    private removeFilter = (
        extractorId: string,
        filter: SimpleFieldFilter,
        excluded: boolean
    ) => {
        if (this.state.filter) {
            const newFilter = removeFieldFilter(
                extractorId,
                filter,
                excluded,
                this.state.filter
            );
            this.setState({ filter: newFilter });
            if (this.props.onFilterChanged) {
                this.props.onFilterChanged(newFilter);
            }
        }
    };

    private resetFilter = () => {
        this.setState({ filter: null });
        if (this.props.onFilterChanged) {
            this.props.onFilterChanged(null);
        }
    };

    private renderExpandedRow = (
        item: ITEM,
        index: number,
        _indent: number,
        _expanded: boolean
    ): React.ReactNode => {
        const { onExpand } = this.props;
        if (!onExpand) {
            return null;
        }
        const expanded = onExpand(item, index);
        const headers = expanded.dataView.extractorGroups.map((group) => {
            return group.extractors.map((extractor) => {
                const builder = expanded.columnBuilders[extractor.id];
                let style;
                if (builder) {
                    const columnBuilder = builder(extractor);
                    style = columnBuilder.layout && {
                        width: `${columnBuilder.layout.fixedWidth}px`,
                    };
                }
                return (
                    <th
                        key={`${group.id}_${extractor.id}`}
                        style={style}
                        className={theme.expandedHeader}
                    >
                        {extractor.label}
                    </th>
                );
            });
        });
        const rows: Array<React.ReactNode> = [];
        DV.forEach(expanded.dataView.dataSource.iter(), (row) => {
            const rowId = expanded.dataView.dataSource.getId(row);
            rows.push(
                <tr key={`row_${rowId}`}>
                    {expanded.dataView.extractorGroups.map((group) => {
                        return group.extractors.map((extractor) => {
                            const builder =
                                expanded.columnBuilders[extractor.id];
                            let value: React.ReactNode;
                            let style;
                            if (builder) {
                                const columnBuilder = builder(extractor);
                                const format = columnBuilder.getFormattedValue;
                                if (format) {
                                    value = format(row);
                                }
                                style = columnBuilder.layout && {
                                    width: `${columnBuilder.layout.fixedWidth}px`,
                                };
                            }
                            if (!value) {
                                value = extractor.asString(row);
                            }
                            return (
                                <td
                                    key={`${group.id}_${extractor.id}`}
                                    style={style}
                                >
                                    {value}
                                </td>
                            );
                        });
                    })}
                </tr>
            );
        });
        return (
            <table className={theme.expandedTable}>
                <thead>
                    <tr>{headers}</tr>
                </thead>
                <tbody>{rows}</tbody>
            </table>
        );
    };

    private buildPopup = () => {
        const { popup } = this.state;
        if (!popup) {
            return null;
        }
        return (
            <ExportPopup
                selectedIds={popup.selectedIds}
                title={popup.title}
                onConfirm={popup.onConfirm}
                onCancel={() => {
                    this.setState({ popup: undefined });
                }}
            />
        );
    };

    private buildDrawer = () => {
        const { drawer } = this.state;
        if (!drawer) {
            return null;
        }
        let title: string;
        let width: "DEFAULT" | "LARGE" = "DEFAULT";
        switch (drawer.type) {
            case "DETAILS":
                title = t("ChangeDetails");
                break;
            case "ACCESS_RIGHTS":
                title = t("ManageAccessRights");
                break;
            case "MODIFY_SCHEDULE":
                title = t("ModifySchedule");
                width = "LARGE";
                break;
            default:
                return assertNever(drawer);
        }
        return (
            <ResourcesDrawer
                title={title}
                selectedIds={drawer.selectedIds}
                readOnly={drawer.readOnly}
                readOnlyInfo={drawer.readOnlyInfo}
                renderBody={drawer.renderBody}
                confirmationTitle={drawer.confirmationTitle}
                confirmationText={drawer.confirmationText}
                width={width}
                onCancel={() =>
                    this.setState({
                        drawer: undefined,
                    })
                }
                onSave={(form, closeDialog) => {
                    // Protect against the crazy
                    if (drawer.readOnly || !(drawer.selectedIds.length > 0)) {
                        console.error("Unexpected save call", drawer);
                        throw new Error("Unexpected save call");
                    }
                    drawer.onSave(
                        drawer.selectedIds,
                        form,
                        (closeDrawer: boolean) => {
                            if (closeDrawer) {
                                // Close the drawer, so don't need to close the dialog
                                this.setState({ drawer: undefined });
                            } else {
                                closeDialog();
                            }
                        }
                    );
                }}
            />
        );
    };

    getExpandedRowClassName = () => {
        return theme.expandedRow;
    };
}

export {
    ControlResources,
    type ControlResourcesProps,
    type ExpandedRow,
    type ExportAction,
};
